'use client'

import { cx } from 'cva'
import { useId, useState, type ComponentProps, type FC } from 'react'
import { useFormStatus } from 'react-dom'
import { IconCircle } from '../atoms/IconCircle'

export type SearchBarProps = Pick<
	ComponentProps<'input'>,
	'value' | 'onChange' | 'defaultValue'
> & {
	theme?: 'white' | 'blue'
	loading?: boolean
	border?: boolean
	autoFocus?: boolean
}

export const SearchBar: FC<SearchBarProps> = ({
	loading = false,
	border = false,
	autoFocus = false,
	onChange,
	...props
}) => {
	const id = useId()

	const [empty, setEmpty] = useState(!props.defaultValue)
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setEmpty(e.target.value === '')
		onChange?.(e)
	}

	const formStatus = useFormStatus()
	const submitting = formStatus.action === '/search' && formStatus.pending

	return (
		<div
			className={cx(
				'relative w-full flex items-center bg-white rounded-sm',
				border && 'border-(length:--stroke-sm) border-blue focus-within:border-blue-shade-70',
			)}
		>
			<label htmlFor={id} className="sr-only">
				Search
			</label>
			<input
				ref={(node) => {
					// Autofocus to end of input text
					if (node && autoFocus) {
						const value = node.value
						node.value = ''
						node.focus()
						node.value = value
					}
				}}
				id={id}
				type="text"
				name="query"
				required
				placeholder="Search GIICA.au..."
				className="peer w-full border-none outline-none text-display text-blue-shade-70 placeholder:text-blue-tint-60 h-19 indent-xl"
				onChange={handleChange}
				{...props}
			/>
			<button type="submit" className="group mx-lg">
				{loading || submitting ? (
					<IconCircle
						icon={['fas', 'spinner-third']}
						intent={empty ? 'secondary' : 'primary'}
						className="animate-spin"
						aria-hidden="true"
					/>
				) : (
					<IconCircle
						icon={['fas', 'search']}
						intent={empty ? 'secondary' : 'primary'}
						aria-hidden="true"
					/>
				)}
			</button>
		</div>
	)
}
